/**
 * V4 Editor Types
 *
 * Types specific to the V4 CMS editor module.
 * Mirrors backend types but adds frontend-specific concerns.
 */

// =============================================================================
// EDITOR STATE
// =============================================================================

/** Editor mode: admin (dashboard) or customer (portal) */
export type EditorMode = 'admin' | 'customer';

/** Viewport sizes for responsive preview */
export type EditorViewport = 'desktop' | 'tablet' | 'mobile';

/** Viewport width in pixels */
export const VIEWPORT_WIDTHS: Record<EditorViewport, number> = {
  desktop: 1440,
  tablet: 768,
  mobile: 375,
};

/** Editor state */
export interface EditorState {
  /** Currently selected page slug */
  activePage: string;
  /** Currently selected element (for inline editing) */
  selectedElement: string | null;
  /** Viewport size for preview */
  viewport: EditorViewport;
  /** Zoom level (percentage) */
  zoomLevel: number;
  /** Number of unsaved changes */
  dirtyCount: number;
  /** Whether the editor is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

// =============================================================================
// CONTENT MODEL (mirrors backend types for the frontend)
// =============================================================================

/** Editable element types */
export type EditableElementType = 'text' | 'image' | 'link' | 'list' | 'rich-text';

/** Single editable element */
export interface EditableElement {
  id: string;
  type: EditableElementType;
  path: string;
  value: string | string[];
  editable: boolean;
  adminOnly?: boolean;
  label: string;
  placeholder?: string;
  /**
   * Image framing: CSS `object-position` as "X% Y%".
   *
   * Absent means the browser default "50% 50%" — the centre crop images have
   * always had — so unframed content needs no migration. Mirrors
   * EditableElement.position in the orchestrator.
   */
  position?: string;
  constraints?: {
    maxLength?: number;
    minLength?: number;
    allowHtml?: boolean;
    allowLineBreaks?: boolean;
    imageTypes?: string[];
  };
}

/** Section with editable elements */
export interface EditableSection {
  id: string;
  type: string;
  label: string;
  variant?: string;
  elements: EditableElement[];
  isStructural?: boolean;
}

/** Page with sections */
export interface EditablePage {
  slug: string;
  title: string;
  description: string;
  keywords?: string;
  sections: EditableSection[];
}

/** Full editable site content */
export interface EditableSiteContent {
  version: string;
  websiteId: string;
  businessName: string;
  recipe: Record<string, unknown>;
  pages: Record<string, EditablePage>;
  navigation: {
    pages: Array<{
      slug: string;
      label: string;
      visible: boolean;
    }>;
  };
  footer: {
    businessName: string;
    tagline?: string;
    social?: Record<string, string>;
  };
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/** Page info for the page selector */
export interface EditorPageInfo {
  slug: string;
  title: string;
  sectionCount: number;
  hasRawHtml?: boolean;
}

/** Website info from the editor API */
export interface EditorWebsiteInfo {
  id: string;
  businessName: string;
  status: string;
  cloudflareUrl?: string;
}

/** Version info */
export interface EditorVersionInfo {
  id: string;
  number: number;
  createdAt: string;
  commitMessage: string | null;
}

/** Response from GET /api/editor/:websiteId/content */
export interface EditorContentResponse {
  success: boolean;
  hasContent: boolean;
  message?: string;
  website?: EditorWebsiteInfo;
  version?: EditorVersionInfo;
  editableContent?: EditableSiteContent;
  rawSections?: Record<string, string>;
  pageList?: EditorPageInfo[];
}

/** Response from GET /api/editor/:websiteId/pages */
export interface EditorPagesResponse {
  success: boolean;
  pages: EditorPageInfo[];
}

// =============================================================================
// IFRAME COMMUNICATION
// =============================================================================

/** Messages sent from parent to iframe */
export type ParentToIframeMessage =
  | { type: 'enable-editing' }
  | { type: 'disable-editing' }
  | { type: 'select-element'; elementId: string }
  | { type: 'update-element'; elementId: string; newValue: string }
  | { type: 'highlight-section'; sectionId: string };

/** Messages sent from iframe to parent */
export type IframeToParentMessage =
  | { type: 'element-clicked'; elementId: string; value: string; rect: DOMRect }
  | { type: 'section-clicked'; sectionId: string; sectionType: string }
  | { type: 'element-hovered'; elementId: string | null }
  | { type: 'ready' };
